import { Controller } from "@hotwired/stimulus";
import {
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import diagnosticRangeBackgroundPlugin from "./diagnostic_range_background_plugin";

// register only what's needed for line charts
Chart.register(
  CategoryScale,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  diagnosticRangeBackgroundPlugin,
);

export default class extends Controller {
  static values = {
    type: String,
    data: Object,
    options: { type: Object, default: {} },
  };

  static targets = ["canvas"];

  connect() {
    const chartData = buildChartData(this.dataValue);
    const chartOptions = buildChartOptions(
      this.optionsValue,
      chartData,
      this.dataValue?.diagnostic_boundaries,
    );

    this.chart = new Chart(this.canvasTarget.getContext("2d"), {
      type: this.typeValue,
      data: chartData,
      options: chartOptions,
    });
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy();
    }
  }
}

function buildChartData(rawData) {
  if (!rawData || typeof rawData !== "object") {
    return rawData;
  }

  // strip out diagnostic_boundaries from the chart data, since it's not a valid Chart.js property
  const { diagnostic_boundaries, ...chartData } = rawData;
  return chartData;
}

function buildChartOptions(rawOptions, chartData, boundaries) {
  const options = { ...rawOptions, plugins: { ...(rawOptions?.plugins ?? {}) } };

  // If the diagnostic boundaries are present, we want to add the diagnostic range background
  // plugin to the chart.
  if (boundaries?.bands?.length > 0) {
    options.plugins.diagnosticRange = { ...options.plugins.diagnosticRange, bands: boundaries.bands };
  }

  // Remove the legend from the chart, since we already display the name of the analyte.
  options.plugins.legend = { display: false, ...options.plugins?.legend };

  // Offsetting the scale insets the data points half a category width from each edge of the chart,
  // so their labels fit within the canvas width and the data points don't meet at the edge of the
  // chart area.
  options.scales = {
    ...(options.scales ?? {}),
    x: {
      offset: true,
      ...options.scales?.x,
    },
  };

  // If neither the min nor max is set in the options, we use the diagnostic boundaries to set them.
  // If the min and max are already set in the options, we don't override them.
  if (!Number.isFinite(boundaries?.min) && !Number.isFinite(boundaries?.max)) {
    return options;
  }

  // Hide the y-axis tick labels and collapse the y-axis width to 0, so the mirrored labels are 
  // drawn over the diagnostic range background instead of to the left of it.
  // We also set the min and max of the y-axis to the diagnostic boundaries. 
  options.scales.y = {
    ...options.scales?.y,
    min: Number.isFinite(options.scales?.y?.min) ? options.scales.y.min : boundaries.min,
    max: Number.isFinite(options.scales?.y?.max) ? options.scales.y.max : boundaries.max,
    ticks: { display: false, ...options.scales?.y?.ticks },
    afterFit: (scale) => {
      scale.width = 0;
    },
  };

  return options;
}
